import { useEffect, useState } from "react";
import { Wand2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useAppStore } from "@/store/app";
import { AppealTypePicker } from "@/components/AppealTypePicker";
import { appealTypesLabel } from "@/lib/document-kinds";
import {
  useBundlePlan,
  useGenerateSynthetic,
  useGetSession,
  useSetAppealType,
} from "@/hooks/api";
import { ensureSession } from "@/lib/session";
import type {
  DocumentKind,
  AppealType,
  AppealTypeSelection,
} from "@/api/types";

// Lead with our strongest appeal type (technical / no-auth), not the
// clinical medical-necessity bucket — see APPEAL_TYPES ordering.
const DEFAULT_PRIMARY: AppealType = "no-authorization";

function token() {
  return Math.random().toString(36).slice(2);
}

/** Build the API selection list — index 0 is the primary. */
function buildSelections(appealTypes: AppealType[]): AppealTypeSelection[] {
  return appealTypes.map((appeal_type) => ({ appeal_type }));
}

export function AppealTypeDialog() {
  const open = useAppStore((s) => s.appealTypeDialogOpen);
  const close = useAppStore((s) => s.closeAppealTypeDialog);
  const target = useAppStore((s) => s.appealTypePickerTarget);
  const sessionId = useAppStore((s) => s.sessionId);
  const selectDoc = useAppStore((s) => s.selectDoc);
  const addPending = useAppStore((s) => s.addPending);
  const removePending = useAppStore((s) => s.removePending);

  const { data: session } = useGetSession(sessionId);
  const lockedAppealTypes = session?.appeal_types ?? null;
  const isLocked = !!lockedAppealTypes && lockedAppealTypes.length > 0;

  const { setAppealType, isPending: appealTypePending } = useSetAppealType(sessionId);
  const { generateSynthetic, isPending: genPending } = useGenerateSynthetic();
  const { bundlePlan } = useBundlePlan();
  const [bundleRunning, setBundleRunning] = useState(false);
  const submitting = appealTypePending || genPending || bundleRunning;

  const [selected, setSelected] = useState<AppealType[]>(
    lockedAppealTypes && lockedAppealTypes.length ? lockedAppealTypes : [DEFAULT_PRIMARY],
  );

  // Sync selection with the store every time the dialog opens — keeps a
  // freshly-opened dialog showing the right selection rather than the
  // value from the first mount.
  useEffect(() => {
    if (open) {
      setSelected(
        lockedAppealTypes && lockedAppealTypes.length
          ? lockedAppealTypes
          : [DEFAULT_PRIMARY],
      );
    }
  }, [open, lockedAppealTypes]);

  const isBundle = target?.mode === "bundle";
  const primary = selected[0] ?? null;

  function onToggle(s: AppealType) {
    if (isLocked) return;
    setSelected((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  function onMakePrimary(s: AppealType) {
    if (isLocked) return;
    setSelected((prev) => [s, ...prev.filter((x) => x !== s)]);
  }

  const label = appealTypesLabel(selected) ?? "—";

  async function onConfirmSingle(kind: DocumentKind) {
    try {
      const sid = await ensureSession();
      const selections = buildSelections(selected);
      await setAppealType({ selections });
      const parsed = await generateSynthetic({ sessionId: sid, kind, selections });
      toast.success(`Generated ${parsed.info.filename} (${label})`);
      close();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Generation failed: ${msg}`);
    }
  }

  async function onConfirmBundle() {
    setBundleRunning(true);
    let tokens: { kind: DocumentKind; token: string }[] = [];
    try {
      const sid = await ensureSession();
      const selections = buildSelections(selected);
      // Ask the backend which documents this appeal type needs — base
      // claim docs + the supporting docs the selection requires. Keeping
      // the plan server-side means the bundle can't drift from what the
      // agent's required-docs logic expects, so the LLM won't ask the
      // user to upload more files after a sample bundle is generated.
      const kinds = await bundlePlan({ sessionId: sid, selections });
      tokens = kinds.map((kind) => {
        const t = token();
        addPending({ token: t, kind, mode: "synthesize" });
        return { kind, token: t };
      });
      // Close once the optimistic placeholders exist so the doc rail
      // shows generation progress while it runs in the background.
      close();
      let firstId: string | null = null;
      for (let i = 0; i < tokens.length; i++) {
        const { kind, token: t } = tokens[i];
        try {
          // First call carries the selections to lock the session;
          // later calls inherit from session.appeal_types.
          const parsed = await generateSynthetic({
            sessionId: sid,
            kind,
            ...(i === 0 ? { selections } : {}),
          });
          if (!firstId) firstId = parsed.info.id;
        } finally {
          removePending(t);
        }
      }
      if (firstId) selectDoc(firstId);
      toast.success(`Generated ${label} sample (${tokens.length} docs)`);
    } catch (e) {
      tokens.forEach(({ token: t }) => removePending(t));
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Sample generation failed: ${msg}`);
    } finally {
      setBundleRunning(false);
    }
  }

  async function onConfirm() {
    if (!target) return close();
    if (selected.length === 0) {
      toast.error("Pick at least one denial reason.");
      return;
    }
    if (target.mode === "bundle") {
      await onConfirmBundle();
    } else {
      await onConfirmSingle(target.kind as DocumentKind);
    }
  }

  const title = isBundle
    ? "Pick appeal types for the sample bundle"
    : isLocked
      ? "Appeal type is locked"
      : "Pick appeal type(s)";
  const description = isBundle
    ? "Generates a full sample claim — the EDI files plus the supporting documents the chosen appeal type(s) actually need, so the draft has everything it requires. The session is locked to this selection once generation starts."
    : isLocked
      ? "This session already has documents generated under one selection. The appeal types can't be changed without starting a new session."
      : "Pick one or more denial reasons. Multiple selections produce a mixed denial — each reason's CARC is layered across the claim's lines. You can't change it once the first synthetic document is created.";
  const confirmLabel = submitting
    ? "Generating…"
    : isBundle
      ? "Generate sample bundle"
      : `Generate with ${primary ? label : "…"}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="leading-relaxed">
            {description}
          </DialogDescription>
        </DialogHeader>

        <AppealTypePicker
          selected={selected}
          onToggle={onToggle}
          onMakePrimary={onMakePrimary}
          lockedAppealTypes={lockedAppealTypes}
        />

        <DialogFooter className="!justify-between">
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting || selected.length === 0}
            className="gap-2"
          >
            <Wand2 className="w-4 h-4" />
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
